<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="slide-captcha-overlay" @click.self="handleOverlayClick">
      <div class="slide-captcha-container" :class="{ 'shake': isError }" ref="containerRef">
        <!-- 头部 -->
        <div class="captcha-header">
          <span class="captcha-title">安全验证</span>
          <button class="captcha-close-btn" @click="close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- 图像区（高度随画布比例自适应） -->
        <div class="captcha-image-area" :style="{ height: imageAreaHeight + 'px' }">
          <!-- 加载中 -->
          <div v-if="loading" class="captcha-loading">
            <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
            <span>正在加载安全验证...</span>
          </div>

          <!-- 图片展示 -->
          <div v-else class="captcha-img-wrapper">
            <img :src="captchaData.bgImage" class="captcha-bg-img" alt="验证码背景" />
            <img 
              :src="captchaData.slideImage" 
              class="captcha-slide-img" 
              :style="{ 
                top: captchaData.y + 'px', 
                left: sliderLeft + 'px',
                width: pieceSize + 'px',
                height: pieceSize + 'px',
                transition: isDragging ? 'none' : 'left 0.3s ease'
              }" 
              alt="拼图滑块" 
            />
            
            <!-- 验证成功/失败遮罩提示 -->
            <div v-if="verified" class="verify-mask success">
              <i class="fa-solid fa-circle-check"></i>
              <span>验证通过</span>
            </div>
            <div v-if="isError && !isDragging" class="verify-mask fail">
              <i class="fa-solid fa-circle-xmark"></i>
              <span>对齐失败，请重试</span>
            </div>

            <!-- 刷新按钮 -->
            <button class="captcha-refresh-btn" @click="loadCaptcha" :disabled="loading || verified">
              <i class="fa-solid fa-rotate"></i>
            </button>
          </div>
        </div>

        <!-- 滑块条 -->
        <div class="captcha-slider-bar" ref="sliderBar">
          <div class="slider-bar-text" :style="{ opacity: isDragging || verified ? 0 : 0.6 }">
            向右滑动，将拼图滑入纹理匹配的缺口
          </div>
          
          <!-- 进度填充色 -->
          <div 
            class="slider-bar-progress" 
            :class="{ 'success': verified, 'fail': isError && !isDragging }"
            :style="{ 
              width: sliderLeft + 20 + 'px',
              transition: isDragging ? 'none' : 'width 0.3s ease'
            }"
          ></div>

          <!-- 拖动滑块 -->
          <div 
            class="slider-handler"
            :class="{ 'dragging': isDragging, 'success': verified, 'fail': isError && !isDragging }"
            :style="{ 
              left: sliderLeft + 'px',
              transition: isDragging ? 'none' : 'left 0.3s ease'
            }"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <i :class="verified ? 'fa-solid fa-check' : (isError && !isDragging ? 'fa-solid fa-xmark' : 'fa-solid fa-angles-right')"></i>
          </div>
        </div>
      </div>
    </div>
  </transition>
  </teleport>
</template>

<script>
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'

export default {
  name: 'SlideCaptcha',
  emits: ['update:visible', 'success'],
  props: {
    // 是否显示
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      verified: false,
      isError: false,
      captchaData: {
        captchaId: '',
        bgImage: '',
        slideImage: '',
        y: 0
      },
      sliderLeft: 0, // 滑块当前左偏移
      isDragging: false,
      startX: 0,
      startY: 0,
      startTime: 0,
      track: [], // 存放轨迹数据 [[x, y, relativeTime]]
      maxDragDistance: 330, // 380(背景宽) - 50(滑块宽) = 330，加载后按后端返回值更新
      captchaScale: 1, // 移动端缩放比例
      pieceSize: 50, // 拼图块边长（px，后端返回，加载后更新）
      imageAreaHeight: 190 // 验证图区域高度（px，随画布比例自适应）
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.reset();
        this.applyResponsiveScale();
        this.loadCaptcha();
      } else {
        window.removeEventListener('resize', this.applyResponsiveScale);
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.applyResponsiveScale);
  },
  methods: {
    reset() {
      this.verified = false;
      this.isError = false;
      this.sliderLeft = 0;
      this.isDragging = false;
      this.track = [];
    },
    // 根据视口宽度计算缩放比例，移动端等比缩小容器
    applyResponsiveScale() {
      this.$nextTick(() => {
        const vw = window.innerWidth;
        const containerWidth = 420;
        const padding = 32;
        if (vw < containerWidth + padding) {
          this.captchaScale = Math.min(1, (vw - padding) / containerWidth);
        } else {
          this.captchaScale = 1;
        }
        if (this.$refs.containerRef) {
          this.$refs.containerRef.style.transform = `scale(${this.captchaScale})`;
          this.$refs.containerRef.style.transformOrigin = 'center center';
        }
        window.addEventListener('resize', this.applyResponsiveScale);
      });
    },
    // 加载验证码图片
    async loadCaptcha() {
      this.loading = true;
      this.reset();
      try {
        const res = await axios.get(`${serverUrl}/api/captcha`);
        if (res.data && res.data.success) {
          this.captchaData = res.data;
          this.maxDragDistance = (res.data.bgWidth || 380) - (res.data.pieceSize || 50);
          this.pieceSize = res.data.pieceSize || 50;
          this.imageAreaHeight = res.data.bgHeight || 190;
        } else {
          this.$message?.error('验证码加载失败，请刷新重试');
        }
      } catch (err) {
        console.error('Failed to load slide captcha:', err);
        this.$message?.error('服务异常，验证码加载失败');
      } finally {
        this.loading = false;
      }
    },
    // 开始拖拽
    startDrag(event) {
      if (this.loading || this.verified || this.isError) return;
      
      this.isDragging = true;
      this.isError = false;
      this.track = [];
      
      const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
      const clientY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;
      this.startX = clientX;
      this.startY = clientY;
      this.startTime = Date.now();
      
      // 添加第一个轨迹点
      this.track.push([0, 0, 0]);

      // 绑定全局鼠标/触摸移动事件
      if (event.type === 'touchstart') {
        document.addEventListener('touchmove', this.onDragMove, { passive: false });
        document.addEventListener('touchend', this.onDragEnd);
      } else {
        document.addEventListener('mousemove', this.onDragMove);
        document.addEventListener('mouseup', this.onDragEnd);
      }
    },
    // 拖拽中
    onDragMove(event) {
      if (!this.isDragging) return;
      
      // 阻止触摸模式下的页面跟随滚动
      if (event.type === 'touchmove') {
        event.preventDefault();
      }

      const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
      const clientY = event.type === 'touchmove' ? event.touches[0].clientY : event.clientY;
      
      let moveX = (clientX - this.startX) / this.captchaScale;
      let moveY = (clientY - this.startY) / this.captchaScale;
      
      // 边界限制
      if (moveX < 0) moveX = 0;
      if (moveX > this.maxDragDistance) moveX = this.maxDragDistance;
      
      this.sliderLeft = moveX;
      
      // 记录轨迹点: [x, y, 相对时间]
      const relativeTime = Date.now() - this.startTime;
      this.track.push([Math.round(moveX), Math.round(moveY), relativeTime]);
    },
    // 拖拽结束
    async onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      // 移除事件监听
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
      document.removeEventListener('touchmove', this.onDragMove);
      document.removeEventListener('touchend', this.onDragEnd);

      // 上报校验
      await this.verifyCaptcha();
    },
    // 调用后端接口验证
    async verifyCaptcha() {
      try {
        const res = await axios.post(`${serverUrl}/api/captcha/verify`, {
          captchaId: this.captchaData.captchaId,
          dragX: this.sliderLeft,
          track: this.track
        });
        
        if (res.data && res.data.success && res.data.token) {
          this.verified = true;
          // 延迟 800ms 关闭弹窗，让用户看清“验证通过”的动效
          setTimeout(() => {
            this.$emit('success', res.data.token);
            this.close();
          }, 800);
        } else {
          // 验证失败，显示错误动效并在 1 秒后重置刷新
          this.isError = true;
          this.$message?.warning(res.data.error || '验证失败，请重试');
          setTimeout(() => {
            this.loadCaptcha();
          }, 1000);
        }
      } catch (err) {
        console.error('Slide verify error:', err);
        this.isError = true;
        this.$message?.error(err.response?.data?.error || '人机验证异常，请重试');
        setTimeout(() => {
          this.loadCaptcha();
        }, 1000);
      }
    },
    close() {
      this.$emit('update:visible', false);
    },
    handleOverlayClick() {
      // 在正在校验或拖拽时，不能关闭弹窗
      if (this.isDragging || this.verified) return;
      this.close();
    }
  }
}
</script>

<style scoped>
/* 磨砂玻璃遮罩层 */
.slide-captcha-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: var(--z-captcha);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* 验证容器 */
.slide-captcha-container {
  width: 420px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 24px;
  padding: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
  position: relative;
}

/* 暗色主题支持 */
body.dark-theme .slide-captcha-container {
  background: rgba(30, 30, 30, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}

/* 头部样式 */
.captcha-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.captcha-title {
  font-family: '爱点风雅黑', sans-serif;
  font-weight: 600;
  font-size: 1.1em;
  color: inherit;
}

.captcha-close-btn {
  background: none;
  border: none;
  font-size: 1.2em;
  color: #888;
  cursor: pointer;
  transition: color 0.2s;
  padding: 4px;
}

.captcha-close-btn:hover {
  color: #ff4d4f;
}

/* 图像渲染区域 */
.captcha-image-area {
  width: 380px;
  height: 190px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.captcha-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #667eea;
  font-family: '猫啃网糖圆体';
  font-size: 0.9em;
  gap: 10px;
}

.captcha-img-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.captcha-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.captcha-slide-img {
  position: absolute;
  width: 50px;
  height: 50px;
  object-fit: contain;
  z-index: 10;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
}

/* 刷新按钮 */
.captcha-refresh-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 15;
}

.captcha-refresh-btn:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: rotate(90deg);
}

/* 验证成功/失败全景遮罩层 */
.verify-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  font-family: '爱点风雅黑';
  font-weight: bold;
  font-size: 1.1em;
  gap: 8px;
  z-index: 20;
  animation: fadeIn 0.3s;
}

.verify-mask.success {
  background: rgba(76, 175, 80, 0.85);
}

.verify-mask.fail {
  background: rgba(244, 67, 54, 0.85);
}

/* 滑动长条 */
.captcha-slider-bar {
  width: 380px;
  height: 40px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  margin-top: 18px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

body.dark-theme .captcha-slider-bar {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.02);
}

.slider-bar-text {
  font-family: '猫啃网糖圆体', sans-serif;
  font-size: 0.85em;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.2s;
}

/* 轨道进度染色 */
.slider-bar-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: rgba(102, 126, 234, 0.35);
  z-index: 1;
}

.slider-bar-progress.success {
  background: rgba(76, 175, 80, 0.4) !important;
}

.slider-bar-progress.fail {
  background: rgba(244, 67, 54, 0.4) !important;
}

/* 拖拽块 */
.slider-handler {
  position: absolute;
  top: 0;
  left: 0;
  width: 50px;
  height: 38px;
  background: #fff;
  border-radius: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 5;
  color: #666;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.slider-handler.dragging {
  cursor: grabbing;
  background: #667eea;
  color: #fff;
}

.slider-handler.success {
  background: #4CAF50 !important;
  color: #fff !important;
  border-color: #4CAF50 !important;
}

.slider-handler.fail {
  background: #f44336 !important;
  color: #fff !important;
  border-color: #f44336 !important;
}

/* 震动动画 */
.shake {
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 路由淡入淡出 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
